import { useRef, useState } from 'react'
import { ImageUp, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Textarea } from '@orisini/ui/components/textarea'
import {
  QrTidakTerbaca,
  decodeQrDariGambar,
  gambarDariPaste,
  tampakSepertiQris,
} from '../lib/decode-qr'

/**
 * Input payload QRIS statis: unggah/tarik/tempel GAMBAR QR, atau tempel teks
 * payload langsung. Gambar didecode di browser lalu hasilnya mengisi textarea —
 * yang dikirim ke server tetap teksnya, jadi validasi CRC di backend tetap
 * menjadi penentu akhir.
 */

export function QrisPayloadInput({
  value,
  onChange,
}: {
  value: string
  onChange: (payload: string) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [sibuk, setSibuk] = useState(false)
  const [seret, setSeret] = useState(false)

  async function prosesGambar(file: File) {
    setSibuk(true)
    try {
      const payload = await decodeQrDariGambar(file)
      onChange(payload)
      if (tampakSepertiQris(payload)) {
        toast.success('QR read — check the contents, then save')
      } else {
        toast.warning("QR read, but the contents don't look like a QRIS payload", {
          description: "Make sure the photo is the merchant's payment QR, not another QR.",
        })
      }
    } catch (err) {
      toast.error(err instanceof QrTidakTerbaca ? err.message : "Couldn't read the image")
    } finally {
      setSibuk(false)
    }
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setSeret(true)
        }}
        onDragLeave={() => setSeret(false)}
        onDrop={(e) => {
          e.preventDefault()
          setSeret(false)
          const file = e.dataTransfer.files[0]
          if (file) void prosesGambar(file)
        }}
        onPaste={(e) => {
          const file = gambarDariPaste(e)
          if (file) {
            e.preventDefault()
            void prosesGambar(file)
          }
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
          seret ? 'border-primary bg-primary/5' : ''
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void prosesGambar(file)
            // Direset supaya memilih file yang sama dua kali tetap memicu onChange.
            e.target.value = ''
          }}
        />
        {sibuk ? (
          <>
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Reading QR…</p>
          </>
        ) : (
          <>
            <ImageUp className="size-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drag a QR image here, paste a screenshot (Ctrl+V), or
            </p>
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              Choose QR image
            </Button>
            <p className="text-xs text-muted-foreground">
              A photo of the QRIS receipt/sticker, a screenshot, or a PNG/JPG file from the provider
            </p>
          </>
        )}
      </div>

      <Textarea
        rows={4}
        className="font-mono text-xs"
        placeholder="…or paste the contents (text payload) of your merchant's static QRIS QR directly here"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}
